/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { debounce } from "utils/lodashUtils";
import { TAB } from "dc-preview-utils/src/Constants/key-codes";
import { pdfEditOperations } from "dc-preview-utils/src/Constants/enums";
import { apiNames } from "constants/apiNames";
import { onPDFAction } from "pdfActionManager/listenToPDFAction";
import { getNewYPosition, getNewXPosition } from "pdfActionManager";
import { apiFactory } from "api/external/ApiFactory";
import {
  ACCESSIBILITY_CACHE_PRIORITY,
  ACCESSIBILITY_TEXT_EXTRACT_PRIORITY,
  ACCESSIBILITY_CACHING_WINDOW,
  RELOAD_ACCESSIBILITY_DEBOUNCE_WAIT
} from "constants/constants";
import { getPagesViewYPos, getPagesViewXPos } from "selectors/pagedViewState";
import { getAccContainerElementHandle, shouldFetchAccContent } from "selectors/accessibilitySelectors";
import { isReadAloudEnabled } from "selectors/readAloudSelectors";
import { previewEvents } from "publicApis/Events/previewEvents";
import { eventNames } from "publicApis/constants";
import { ContentTabbingManager } from "accessibility/ContentTabbingManager";
import { SequentialLoader } from "accessibility/SequentialLoader";
import { getTotalPages, getPageHandle, getPagesInView } from "selectors/documentSelectors";
import { isMarkupAnnot } from "../utils/QuickToolsUtils";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";

const subscribeToPDFEditOperations = async callback => {
  const docApis = await apiFactory.getExposedApis("preview", [apiNames.DOCUMENT], true);
  docApis && docApis[apiNames.DOCUMENT] && docApis[apiNames.DOCUMENT].subscribeToPDFEditOperations(callback);
};

class AccessibilityManager {
  constructor(stores) {
    this.stores = stores;
    this.accDataLoader = new SequentialLoader(this._loadAccData);
    this.onPDFAction = this.onPDFAction.bind(this);
    onPDFAction(this.onPDFAction);

    this.pagesToReloadAccText = new Set();
    this.onPDFEdit = this.onPDFEdit.bind(this);
    if (isReadAloudEnabled(stores) || shouldFetchAccContent(stores)) subscribeToPDFEditOperations(this.onPDFEdit);

    this.contentTabbingManager = new ContentTabbingManager(stores);
    window.addEventListener("focusin", this.onFocus.bind(this));

    subscribeToSelector(stores, shouldFetchAccContent, shouldFetch => {
      if (shouldFetch) {
        window.addEventListener("keydown", this.onKeydown.bind(this));
        const { start, end } = getPagesInView(this.stores);
        this.onPagesInViewChange({ startPage: { pageNumber: start + 1 }, endPage: { pageNumber: end + 1 } });
        previewEvents.on(eventNames.PAGES_IN_VIEW_CHANGE, this.onPagesInViewChange);
      }
    });
  }

  debouncedLoadPageAccText = debounce(() => {
    this.pagesToReloadAccText.forEach(pageIndex =>
      this.stores.documentStore.loadPageAccText(pageIndex, ACCESSIBILITY_TEXT_EXTRACT_PRIORITY)
    );
    this.pagesToReloadAccText.clear();
  }, RELOAD_ACCESSIBILITY_DEBOUNCE_WAIT);

  onPDFEdit(eventName, params) {
    const {
      FAS_FIELD_CREATED,
      FAS_FIELD_DELETED,
      FAS_FIELD_EDITED,
      TEXT_EDITED,
      IMAGE_EDITED,
      COMMENT_CREATED,
      COMMENT_DELETED,
      COMMENT_EDITED
    } = pdfEditOperations;
    const editModeOps = [TEXT_EDITED, IMAGE_EDITED];
    const fasOps = [FAS_FIELD_CREATED, FAS_FIELD_DELETED, FAS_FIELD_EDITED];
    const commentOps = [COMMENT_CREATED, COMMENT_DELETED];

    if (
      shouldFetchAccContent(this.stores) &&
      eventName === COMMENT_EDITED &&
      isMarkupAnnot(params.data.annot.type.toLowerCase())
    ) {
      this.stores.accessibilityStore.handleCommentUpdated(params);
      return;
    }
    if (!(editModeOps.includes(eventName) || fasOps.includes(eventName) || commentOps.includes(eventName))) return; // operation does not require reload
    if (fasOps.includes(eventName) && params && params.data && params.data.field && params.data.field.type !== "text")
      return; // fas non-text type operation

    if (params.pageNum > 0) this.stores.documentStore.unloadPageAccData(params.pageNum - 1);
    if (
      shouldFetchAccContent(this.stores) &&
      (fasOps.includes(eventName) ||
        (commentOps.includes(eventName) && (!params.data.annot || isMarkupAnnot(params.data.annot.type.toLowerCase()))))
    ) {
      this.stores.documentStore.loadPageAccText(params.pageNum - 1, ACCESSIBILITY_TEXT_EXTRACT_PRIORITY);
    }
    if (shouldFetchAccContent(this.stores) && editModeOps.includes(eventName)) {
      this.pagesToReloadAccText.add(params.pageNum - 1);
      this.debouncedLoadPageAccText();
    }
  }

  onPDFAction(action) {
    const stores = this.stores;
    switch (action.command) {
      case "setNewLocation": {
        const { pageIndex } = action;
        const pagesViewScrollTop = getPagesViewYPos(stores);
        const pagesViewScrollLeft = getPagesViewXPos(stores);
        const currentPageTop = getNewYPosition(stores, pageIndex, 0);
        const currentPageLeft = getNewXPosition(stores, pageIndex, 0);
        const pageHandle = getPageHandle(stores, pageIndex);
        const targetPageAccContainerElementHandle = pageHandle
          ? getAccContainerElementHandle(0, pageHandle)
          : undefined;
        if (targetPageAccContainerElementHandle) {
          const children = targetPageAccContainerElementHandle.children;
          let targetChild = null;
          if (children && children.length) {
            let startChild = 0;
            /**
             * Starting from one since the first element would be
             * the div to read page number.
             */
            if (children.length > 1) {
              startChild = 1;
            }
            for (let i = startChild; i < children.length; ++i) {
              const child = children[i];
              if (
                child.offsetTop + child.offsetHeight + currentPageTop >= pagesViewScrollTop &&
                child.offsetLeft + child.offsetWidth + currentPageLeft >= pagesViewScrollLeft
              ) {
                targetChild = child;
                break;
              }
            }
          }

          if (targetChild) {
            targetChild.focus();
          }
        }
        break;
      }
      default: {
        break;
      }
    }
  }

  onKeydown(event) {
    if (event.keyCode === TAB && this.contentTabbingManager.handleTab(event.shiftKey)) event.preventDefault();
  }

  onPagesInViewChange = eventData => {
    const start = eventData.startPage.pageNumber - 1;
    const end = eventData.endPage.pageNumber - 1;

    const itemsToCache = this._getItemsToCache(
      { start, end },
      getTotalPages(this.stores),
      ACCESSIBILITY_CACHING_WINDOW
    );
    this.accDataLoader.load(itemsToCache, ACCESSIBILITY_CACHE_PRIORITY);
  };

  reset = () => {
    this.accDataLoader.reset();
  };

  _getItemsToCache = (itemsInView, totalItems, cacheWindow) => {
    const itemsToCache = [];
    const overflowItems = [];

    const viewStart = itemsInView.start;
    const viewEnd = itemsInView.end;
    let cacheStart = Math.max(viewStart - cacheWindow, 0);
    let cacheEnd = Math.min(viewEnd + cacheWindow, totalItems - 1);
    for (let k = 1; k <= cacheWindow; ++k) {
      if (viewEnd + k < totalItems) {
        itemsToCache.push(viewEnd + k);
      } else if (cacheStart - 1 >= 0) {
        overflowItems.push(--cacheStart);
      }

      if (viewStart - k >= 0) {
        itemsToCache.push(viewStart - k);
      } else if (cacheEnd + 1 < totalItems) {
        overflowItems.push(++cacheEnd);
      }
    }

    return itemsToCache.concat(overflowItems);
  };

  _loadAccData = (pageIndex, priority) => this.stores.documentStore.loadPageAccText(pageIndex, priority);
  onFocus() {
    this.contentTabbingManager.onFocus();
  }
}

export { AccessibilityManager, subscribeToPDFEditOperations };
